import { API } from "@swivl-mono/api-helper";
import { LocationVarInterface, LocationVarKey } from "@swivl-mono/types";
import { Input, Select, SelectOption, SelectSize } from "@swivl-mono/ui";
import { useCallback, useEffect, useMemo, useState } from "react";

import { SettingRow, SettingsCard } from "../../../../Components/SettingsCard/SettingsCard";
import AppsModel from "../../../../Models/Apps/Apps.Model";

interface Props {
  orgId: number;
}

const CREDENTIAL_KEYS = [LocationVarKey.TwilioApiKey, LocationVarKey.TwilioApiSecret];

/** Org-level outbound Twilio credentials + subaccount picker. Swivl-staff only, gated by the caller. */
export default function TwilioAccountSettings({ orgId }: Props): React.ReactElement {
  const { twilioSubaccounts, isLoadingSubaccounts, isConfiguringSubaccount } = AppsModel.useState();

  const [rowsByKey, setRowsByKey] = useState<Partial<Record<LocationVarKey, LocationVarInterface[]>>>({});
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [draftApiKey, setDraftApiKey] = useState("");
  const [draftApiSecret, setDraftApiSecret] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subaccountError, setSubaccountError] = useState<string | null>(null);
  const [subaccountNote, setSubaccountNote] = useState<string | null>(null);
  // View mode used to print "None" / "—" while the loads were still in flight,
  // which reads as "not set" (ENG-3747 test). Say "Loading…" until each has answered.
  const [credentialsLoaded, setCredentialsLoaded] = useState(false);
  const [subaccountsRequested, setSubaccountsRequested] = useState(false);

  const load = useCallback(async (): Promise<{ key: string; secret: string } | null> => {
    try {
      const [keyRows, secretRows] = await Promise.all(CREDENTIAL_KEYS.map((key) => API.LocationVars.getAllVariablesForKey(orgId, key)));
      setRowsByKey({ [LocationVarKey.TwilioApiKey]: keyRows, [LocationVarKey.TwilioApiSecret]: secretRows });
      const key = keyRows[0]?.value ?? "";
      const secret = secretRows[0]?.value ?? "";
      setApiKey(key);
      setApiSecret(secret);
      setError(null);
      return { key, secret };
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return null;
    } finally {
      setCredentialsLoaded(true);
    }
  }, [orgId]);

  useEffect(() => {
    setCredentialsLoaded(false);
    load();
    setSubaccountsRequested(true);
    AppsModel.loadTwilioSubaccounts();
  }, [load]);

  const saveVar = async (key: LocationVarKey, value: string): Promise<void> => {
    const existing = rowsByKey[key]?.[0];
    if (existing) {
      if (existing.value === value) return;
      await API.LocationVars.updateVariable(orgId, existing.id, value);
    } else if (value) {
      await API.LocationVars.createVariable(key, value, orgId);
    }
  };

  const handleEdit = (): void => {
    setDraftApiKey(apiKey);
    setDraftApiSecret(apiSecret);
    setIsEditing(true);
  };

  const handleCancel = (): void => {
    setIsEditing(false);
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await Promise.all([saveVar(LocationVarKey.TwilioApiKey, draftApiKey), saveVar(LocationVarKey.TwilioApiSecret, draftApiSecret)]);
      await load();
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const subaccountOptions: SelectOption[] = useMemo(() => {
    if (!twilioSubaccounts?.subaccounts) return [];
    return [
      { label: "None", value: "" },
      ...twilioSubaccounts.subaccounts.map((sub) => ({
        label: sub.isRecommended ? `${sub.friendlyName} (Recommended)` : sub.friendlyName,
        value: sub.sid
      }))
    ];
  }, [twilioSubaccounts]);

  const currentSubaccountSid = twilioSubaccounts?.currentSubaccountSid;
  const currentSubaccountLabel = subaccountOptions.find((o) => o.value === currentSubaccountSid)?.label;
  // The configured subaccount's name needs the full list, a slow Twilio call. Until it
  // answers say so; if it never does, the SID is still true and better than "None".
  const subaccountsLoading = !subaccountsRequested || isLoadingSubaccounts;
  const subaccountDisplay = subaccountsLoading
    ? "Loading…"
    : twilioSubaccounts === undefined
      ? currentSubaccountSid || "Couldn't load subaccounts"
      : currentSubaccountLabel || currentSubaccountSid || "None";

  const handleSubaccountChange = async (value: string): Promise<void> => {
    setSubaccountError(null);
    setSubaccountNote(null);
    const { success, softphoneKey } = await AppsModel.configureSubaccount(value);
    if (!success) {
      setSubaccountError("Failed to configure subaccount. Please try again.");
      return;
    }
    // The pick maintains the SUBACCOUNT key pair for CC v3 and never writes the
    // Outbound pair below — that one is the legacy softphone's (ENG-3747).
    // Reload anyway so the card reflects whatever is stored; the drafts follow,
    // or a Save would write a stale value back.
    const fresh = await load();
    if (fresh) {
      setDraftApiKey(fresh.key);
      setDraftApiSecret(fresh.secret);
    }
    if (softphoneKey === "created")
      setSubaccountNote("Created an API key inside this subaccount for the Contact Center softphone. The Outbound Twilio API Key is unchanged.");
    else if (softphoneKey === "copied")
      setSubaccountNote(
        "The Outbound Twilio API Key lives inside this subaccount — copied it to the subaccount key for the Contact Center softphone."
      );
    else if (softphoneKey === "kept")
      setSubaccountNote("The subaccount key for the Contact Center softphone already lives inside this subaccount — kept.");
  };

  return (
    <SettingsCard title="Subaccount Settings" isEditing={isEditing} saving={saving} onEdit={handleEdit} onCancel={handleCancel} onSave={handleSave}>
      {error && <p className="body-sm mx-4 my-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-red-700">Failed: {error}</p>}

      <SettingRow label="Twilio Subaccount" description="Subaccounts matching the org name are shown first as recommended." isEditing={isEditing}>
        {!isEditing ? (
          <span className="body-sm break-words text-grey-900">{subaccountDisplay}</span>
        ) : (
          <Select
            size={SelectSize.MEDIUM}
            options={subaccountOptions}
            selectedValues={currentSubaccountSid ? [currentSubaccountSid] : []}
            onChange={(values) => handleSubaccountChange(values[0] || "")}
            placeholder={isLoadingSubaccounts ? "Loading subaccounts..." : "Select a subaccount"}
            disabled={isLoadingSubaccounts || isConfiguringSubaccount}
            hasSearchInDropdown
          />
        )}
        {isConfiguringSubaccount && <p className="body-sm col-start-2 mt-1 text-grey-600">Configuring subaccount…</p>}
        {subaccountError && <p className="body-sm col-start-2 mt-1 text-red-700">{subaccountError}</p>}
        {subaccountNote && <p className="body-sm col-start-2 mt-1 text-grey-600">{subaccountNote}</p>}
      </SettingRow>

      <SettingRow label="Outbound Twilio API Key" isEditing={isEditing}>
        {!isEditing ? (
          <span className="body-sm break-words text-grey-900">{!credentialsLoaded ? "Loading…" : apiKey || "—"}</span>
        ) : (
          <Input value={draftApiKey} onChange={(e) => setDraftApiKey(e.target.value)} />
        )}
      </SettingRow>

      <SettingRow label="Outbound Twilio API Secret" isEditing={isEditing}>
        {!isEditing ? (
          <span className="body-sm break-words text-grey-900">{!credentialsLoaded ? "Loading…" : apiSecret ? "••••••••" : "—"}</span>
        ) : (
          <Input type="password" value={draftApiSecret} onChange={(e) => setDraftApiSecret(e.target.value)} />
        )}
      </SettingRow>
    </SettingsCard>
  );
}
